import api from './api'
import loggingService from './loggingService'

/**
 * Non-sponsored event tutoring authorization API.
 *
 * Adult participants sign their own authorization; a minor participant's guardian signs in their
 * place; the host signs the host agreement (a minor host's guardian signs for them). The server
 * derives which document governs the event - callers fetch the current document by slug and post a
 * signature with the signer's role. Mirrors the server routes mounted at /api/event-tutoring.
 */

// Versioned ConsentDocument slug for each signer role (mirrors server
// NON_SPONSORED_EVENT_TUTORING_CONFIG.SLUGS / SLUG_BY_ROLE).
export const EVENT_TUTORING_SLUG_BY_ROLE = {
  participant: 'event-tutoring-participant-ack',
  participant_guardian: 'event-tutoring-guardian-consent',
  host: 'event-tutoring-host-agreement',
  host_guardian: 'event-tutoring-guardian-consent'
}

// Short human label per signer role, for headings/buttons.
export const EVENT_TUTORING_ROLE_LABELS = {
  participant: 'Tutoring participation authorization',
  participant_guardian: 'Parent/guardian authorization',
  host: 'Host / tutor agreement',
  host_guardian: 'Parent/guardian authorization'
}

// Consent types each role must grant, no partial consent (mirrors server
// REQUIRED_EVENT_TUTORING_CONSENTS_BY_ROLE). The signing form renders exactly these so the granted
// set always satisfies the server's requirement for the role.
export const EVENT_TUTORING_REQUIRED_CONSENTS_BY_ROLE = {
  participant: [
    'EVENT_TUTORING_PARTICIPATION',
    'PLATFORM_FACILITATION_ACK',
    'RECORDING_AUDIO',
    'RECORDING_VIDEO',
    'CHAT_PARTICIPATION'
  ],
  participant_guardian: [
    'EVENT_TUTORING_PARTICIPATION',
    'PLATFORM_FACILITATION_ACK',
    'RECORDING_AUDIO',
    'RECORDING_VIDEO',
    'CHAT_PARTICIPATION'
  ],
  host: [
    'EVENT_TUTORING_HOSTING',
    'PLATFORM_FACILITATION_ACK',
    'RECORDING_AUDIO',
    'RECORDING_VIDEO',
    'CHAT_PARTICIPATION'
  ],
  host_guardian: [
    'EVENT_TUTORING_HOSTING',
    'PLATFORM_FACILITATION_ACK',
    'RECORDING_AUDIO',
    'RECORDING_VIDEO',
    'CHAT_PARTICIPATION'
  ]
}

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback

const eventTutoringService = {
  /**
   * Fetch the current published version of a document (slug must be an event-tutoring slug).
   * @param {string} slug
   */
  async getDocument(slug) {
    try {
      const response = await api.get(`/event-tutoring/documents/${slug}`)
      return response.data?.document
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to load the authorization document')
      loggingService.error('Error fetching event tutoring document', { slug, message })
      throw new Error(message)
    }
  },

  /**
   * What is still missing before a participant may join (defaults to the authenticated user).
   * @param {string} eventSeriesId
   * @param {string} [participantId]
   */
  async getParticipantReadiness(eventSeriesId, participantId) {
    try {
      const response = await api.get(
        `/event-tutoring/events/${eventSeriesId}/participant-readiness`,
        { params: participantId ? { participantId } : undefined }
      )
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to load authorization status')
      loggingService.error('Error fetching participant readiness', { eventSeriesId, message })
      throw new Error(message)
    }
  },

  /**
   * What is still missing before the host (event creator) is authorized.
   * @param {string} eventSeriesId
   */
  async getHostReadiness(eventSeriesId) {
    try {
      const response = await api.get(`/event-tutoring/events/${eventSeriesId}/host-readiness`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to load host authorization status')
      loggingService.error('Error fetching host readiness', { eventSeriesId, message })
      throw new Error(message)
    }
  },

  /**
   * Record a signature.
   * @param {string} eventSeriesId
   * @param {{ signerRole: string, subjectId?: string, consentTypes: string[], signatureName: string }} payload
   *   signerRole: 'participant' | 'participant_guardian' | 'host' | 'host_guardian'.
   *   subjectId: the party the authorization is about (omit for an adult self-sign).
   * @returns {Promise<{ success: boolean, authorizationId: string }>}
   */
  async sign(eventSeriesId, { signerRole, subjectId, consentTypes, signatureName }) {
    try {
      const response = await api.post(`/event-tutoring/events/${eventSeriesId}/sign`, {
        signerRole,
        subjectId,
        consentTypes,
        signatureName
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to record authorization')
      loggingService.error('Error signing event tutoring authorization', {
        eventSeriesId,
        signerRole,
        message
      })
      // Preserve the server error code (e.g. UNDER_13_BLOCKED) so the UI can react.
      const wrapped = new Error(message)
      wrapped.code = error?.response?.data?.code || null
      throw wrapped
    }
  }
}

export default eventTutoringService
